import { mergeComponentCollections } from '@ea-lab/reactive-json'
import { BuilderHtmlField } from './BuilderHtmlField'
import {
  ResolvedBackground,
  LazyImage,
  ResolvedLink,
  LucideIcon,
  ClassMap,
  MapText,
  TermLabel,
  FormatDate,
  FormatCurrency,
  PublishToggle,
  InlineEditCell,
  DecoupledDeleteButton,
  RolesEditor,
  YoutubeEmbed,
  CountUp,
  NavDropdown,
  MegaMenu,
  NavShell,
  RawSvg,
  BuilderRowField,
  BuilderRowAction,
  BuilderRowToolbar,
  PathLink,
  FloatingDropdown,
  FacetList,
  RangeFacet,
  ActiveFilterChips,
  Pagination,
  SearchField,
  LoadingOverlay,
  FilteredGallery,
  DateRangeFacet,
  DateRangeAutoInterval,
  ChartJs,
  ScoredQuiz,
  RadarChart,
  ChartLoader,
  FilterCount,
  UrlSync,
  DocumentPreference,
  JsonTreeView,
  ScrollShadow,
  Carousel,
  LeafletMap,
  GroupByMonth,
  BuilderUnit,
  FragmentEmbed,
  UnitMount,
} from './primitives'
// Champs d'affichage colorés (read-only) de la liste ES /tasks : les blocs du
// builder les utilisent aussi, et la collection du builder n'hérite pas de celle d'App.jsx.
import { TaskColorBadge, TaskPriorityIcon, TaskAssigneeAvatar, TaskTagsInline } from '../task'
import FuzzySelectField from '../shared/fields/FuzzySelectField'
// Primitives partagées avec les pages YAML : un bloc « fiche » (record éditable)
// s'en sert pour le bouton d'enregistrement du Syncable, l'avatar d'initiales et
// la liste de liens vers des entités. La collection du builder n'hérite pas de
// celle d'App.jsx — sans elles, le bloc rend un « type inconnu ».
import { DataSyncSaveButton } from '../shared/ui/DataSyncSaveButton'
import { InitialsAvatar } from '../shared/ui/InitialsAvatar'
import { EntityLinkList } from '../shared/ui/EntityLinkList'
// Modale de détail des widgets de tableau de bord (clic sur une cellule ou un
// segment) : la collection du builder n'hérite pas de celle d'App.jsx — sans
// elle, le `type: Modal` d'un bloc ne rend RIEN, en silence. Le clic écrivait
// bien la sélection dans le store, mais la modale n'était jamais montée.
import { Modal } from '../shared/ui/Modal'
// Preuve de travail anti-robot des formulaires publics (contact, lettre
// d'information, inscription) : la collection du builder n'hérite pas de celle
// d'App.jsx — sans elle, le `type: AltchaField` d'un bloc ne rend RIEN, en
// silence, et le formulaire ne part jamais (le jeton reste vide).
import { AltchaField } from '../shared/fields/AltchaField'
// Même raison pour la pièce jointe d'un formulaire public : sans elle, le
// `type: Base64FileField` d'un bloc ne rend RIEN, en silence.
import { Base64FileField } from '../shared/fields/Base64FileField'
// Réactions personnalisées : elles ne sont PAS dans le noyau reactive-json et la
// collection du builder n'hérite pas de celle d'App.jsx. Sans elles, les actions
// `esFetch`/`toggleData` d'un bloc (filtres à facettes, tri, pagination) sont
// des `what:` inconnus — le clic ne déclenche rien, en silence.
import { esFetchReaction } from '../shared/reactions/esFetchReaction'
import { toggleDataReaction } from '../shared/reactions/toggleDataReaction'
import { stringifyDataReaction } from '../shared/reactions/stringifyDataReaction'
import { collectFieldReaction } from '../shared/reactions/collectFieldReaction'
import { flattenEsHits } from './dataProcessors/flattenEsHits'
import { parseFragmentBuild } from './dataProcessors/parseFragmentBuild'
import { DataSync as DataSyncFixed } from './rj-overrides/DataSync'
import { GridLayout } from './GridLayout'
import { ColumnsLayout } from './ColumnsLayout'
import { MarkdownContent } from './MarkdownContent'

/**
 * The plugin collection every builder bloc's `ReactiveJsonRoot` renders with
 * (see `BuilderBlockMount`). The builder has its OWN collection (it does NOT
 * inherit the app-wide one in `App.jsx`) — so any primitive a block YAML uses
 * must be listed here. Keep this aligned with `App.jsx`'s collection for
 * components shared between the public site and the builder (e.g. ChartLoader).
 *
 * Extracted from `BuilderCanvas.jsx` so `BuilderBlockMount` can live in its own
 * module — breaking the `BuilderCanvas ↔ BuilderGridLayout` import cycle.
 */
export const builderPlugins = mergeComponentCollections([{
  element: {
    BuilderHtmlField,
    ResolvedBackground,
    LazyImage,
    ResolvedLink,
    LucideIcon,
    ClassMap,
    MapText,
    TermLabel,
    FormatDate,
    FormatCurrency,
    PublishToggle,
    InlineEditCell,
    DecoupledDeleteButton,
    RolesEditor,
    YoutubeEmbed,
    CountUp,
    NavDropdown,
    MegaMenu,
  NavShell,
    RawSvg,
    // Inline list editing: a cell edited in place (emitted by expandEditable
    // for `editable: {field, column}`), the standalone add button, the per-row
    // up/down/insert/remove toolbar.
    BuilderRowField,
    BuilderRowAction,
    BuilderRowToolbar,
    PathLink,
    FloatingDropdown,
    FacetList,
    RangeFacet,
    ActiveFilterChips,
    Pagination,
    SearchField,
    LoadingOverlay,
    FilteredGallery,
    DateRangeFacet,
    DateRangeAutoInterval,
    ChartJs,
    ScoredQuiz,
    RadarChart,
    ChartLoader,
    // Panneaux de filtres des widgets de tableau de bord : le compteur
    // « N filtres » et le multi-select de référentiel.
    FilterCount,
    // Filtres PARTAGÉS d'un tableau de bord : miroir store ↔ URL, seul canal de
    // diffusion entre des cellules qui sont chacune un ReactiveJsonRoot isolé.
    UrlSync,
    // Préférence d'AFFICHAGE du visiteur (taille du texte, contraste, inversion
    // sombre) : miroir store → attributs de <html> + stockage local. Seule façon
    // d'habiller la page ENTIÈRE depuis un bloc, et de tenir la préférence d'une
    // page à l'autre — chaque lien du site recharge le document.
    DocumentPreference,
    FuzzySelectField,
    AltchaField,
    Base64FileField,
    GridLayout,
    ColumnsLayout,
    MarkdownContent,
    JsonTreeView,
    ScrollShadow,
    Carousel,
    LeafletMap,
    GroupByMonth,
    DataSyncSaveButton,
    InitialsAvatar,
    EntityLinkList,
    Modal,
    TaskColorBadge,
    TaskPriorityIcon,
    TaskAssigneeAvatar,
    TaskTagsInline,
    BuilderUnit,
    FragmentEmbed,
    UnitMount,
    // Override core DataSync until reactive-json ≥ 2.5.2 ships the upstream fix
    // (no spurious sync of async-loaded syncables). builderPlugins merges AFTER
    // core, so this wins — and subroots inherit it.
    DataSync: DataSyncFixed,
  },
  reaction: {
    esFetch: esFetchReaction,
    toggleData: toggleDataReaction,
    stringifyData: stringifyDataReaction,
    collectField: collectFieldReaction,
  },
  dataProcessor: {
    'flatten-es-hits': { callback: flattenEsHits, order: 0 },
    'parse-fragment-build': { callback: parseFragmentBuild, order: 0 },
  },
}])
